import React, { useCallback, useState } from 'react'

import {
  ReportFilter,
  ReportFilterOperation,
  ReportValue,
} from '@driftt/api-types'
import { Div, IconButton } from '@driftt/tide-core'
import { Select } from '@driftt/tide-selects'

import { HumanizedFilterOperations } from 'api/report/query/types'
import { FilterableEntityType } from 'hooks/useAvailableFilters'
import {
  IFilterableEntity,
  useFilterableEntities,
} from 'hooks/useFilterableEntities'
import { ReportPage } from 'types/pages'

interface IPropertySelectProps {
  filter: ReportFilter
  allFilterableEntities: Record<string, IFilterableEntity>
  onFilterChanged: (value: FilterableEntityType) => void
  setSelectedProperty: (value: FilterableEntityType) => void
  availableFilterableEntities: string[]
}

const PropertySelect: React.FC<IPropertySelectProps> = ({
  filter,
  allFilterableEntities,
  onFilterChanged,
  setSelectedProperty,
  availableFilterableEntities,
}) => {
  const selectedProperty = availableFilterableEntities.includes(filter.property)
    ? allFilterableEntities[filter.property].property
    : ''
  return (
    <Select
      className="compound-filter--filter-select-property compound-filter--filter-select"
      placeholder={'Select or search…'}
      value={selectedProperty}
      onChange={({ value }) => {
        onFilterChanged(value)
        setSelectedProperty(value)
      }}
      options={availableFilterableEntities.map(
        (entity) => allFilterableEntities[entity].property
      )}
    />
  )
}

interface IOperationSelectProps {
  filter: ReportFilter
  onFilterChanged: (op: ReportFilterOperation) => void
  selectedPropertyOperations: ReportFilterOperation[]
}

const OperationSelect: React.FC<IOperationSelectProps> = ({
  filter,
  onFilterChanged,
  selectedPropertyOperations,
}) => {
  return (
    <Select
      className="compound-filter--filter-select"
      value={Object.entries(HumanizedFilterOperations)
        .map(([key, val]) => {
          return { label: val, value: key }
        })
        .find(({ value }) => value === filter.operation)}
      onChange={({ value }) => onFilterChanged(value)}
      options={selectedPropertyOperations.map((operation) => {
        return { label: HumanizedFilterOperations[operation], value: operation }
      })}
    />
  )
}

interface IValueSelectProps {
  filter: ReportFilter
  onFilterChanged: (values: ReportValue[]) => void
  selectedPropertyOptions: { label: React.ReactNode; value: string | number }[]
}

const ValueSelect: React.FC<IValueSelectProps> = ({
  filter,
  onFilterChanged,
  selectedPropertyOptions,
}) => {
  return (
    <Select
      isMulti
      className="compound-filter--filter-select-value compound-filter--filter-select"
      placeholder={'Select or search…'}
      value={selectedPropertyOptions.filter((option) =>
        filter.values?.includes(option.value)
      )}
      onChange={(values) => onFilterChanged(values.map(({ value }) => value))}
      options={selectedPropertyOptions}
    />
  )
}

interface ICompoundFilterRowProps {
  reportType: ReportPage
  filter: ReportFilter
  updateFilters: (newFilter: ReportFilter) => void
  onDelete: () => void
  availableFilterableEntities: FilterableEntityType[]
}

export default function CompoundFilterRow({
  reportType,
  filter,
  updateFilters,
  onDelete,
  availableFilterableEntities,
}: ICompoundFilterRowProps) {
  const allFilterableEntities = useFilterableEntities(reportType)
  const [selectedProperty, setSelectedProperty] = useState(filter.property)
  const selectedFilterableEntity = allFilterableEntities[selectedProperty]
  const selectedPropertyOperations =
    selectedFilterableEntity.availableOperations
  const selectedPropertyOptions = selectedFilterableEntity.optionList

  const onPropertyChange = useCallback(
    (property: FilterableEntityType) => {
      const newFilter = { ...filter, values: [], property }
      updateFilters(newFilter)
    },
    [filter, updateFilters]
  )

  const onOperationChange = useCallback(
    (operation: ReportFilterOperation) => {
      const newFilter: ReportFilter = { ...filter, operation }
      updateFilters(newFilter)
    },
    [filter, updateFilters]
  )

  const onValueChange = useCallback(
    (values: ReportValue[]) => {
      const newFilter: ReportFilter = { ...filter, values }
      updateFilters(newFilter)
    },
    [filter, updateFilters]
  )

  return (
    <Div
      className="compound-filter--filter-row"
      data-ocweb-id="ReportsCompoundFilterRow"
    >
      <PropertySelect
        filter={filter}
        allFilterableEntities={allFilterableEntities}
        onFilterChanged={onPropertyChange}
        availableFilterableEntities={availableFilterableEntities}
        setSelectedProperty={setSelectedProperty}
      />
      <OperationSelect
        filter={filter}
        onFilterChanged={onOperationChange}
        selectedPropertyOperations={selectedPropertyOperations}
      />
      <ValueSelect
        filter={filter}
        onFilterChanged={onValueChange}
        selectedPropertyOptions={selectedPropertyOptions}
      />
      <IconButton
        className="compound-filter--filter-action"
        icon="cancel-circle"
        onClick={onDelete}
        width="8"
        height="8"
      />
    </Div>
  )
}
